import React, { useState, useRef, useCallback } from 'react';
import './cards.css';

const Rare999Card = ({ player, scale = 1, interactive = true, showBack = false }) => {
  const [flipped, setFlipped] = useState(false);

  const {
    name, team, teamFull, position, number, cityColor,
    playerId, teamLogoId, sport = 'mlb',
    teamColor, nameplateColor,
    photoUrl, photoPosition = 'center 15%',
    fontSize = 18,
    bio = {},
    stats = [],
    statHeaders = [],
    highlights = [],
    cardNumber = '',
    numbering = '/999',
  } = player;


  const headshotUrl = sport === 'nhl'
    ? `https://assets.nhle.com/mugs/nhl/latest/${playerId}.png`
    : `https://img.mlbstatic.com/mlb-photos/image/upload/w_200,q_auto:best/v1/people/${playerId}/headshot/silo/current`;

  const actionUrl = photoUrl || (sport === 'nhl'
    ? `https://assets.nhle.com/mugs/actionshots/1296x729/${playerId}.jpg`
    : `./cards/photos/players/${playerId}.jpg`);

  const teamLogoUrl = sport === 'nhl'
    ? `https://assets.nhle.com/logos/nhl/svg/${team}_light.svg`
    : `https://www.mlbstatic.com/team-logos/${teamLogoId || playerId}.svg`;

  const cardRef = useRef(null);
  const rainbowRef = useRef(null);
  const reflectionRef = useRef(null);

  const lastMove = useRef(0);
  const handleMouseMove = useCallback((e) => {
    const now = Date.now();
    if (now - lastMove.current < 16) return;
    lastMove.current = now;
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    el.style.transform = `perspective(800px) rotateY(${(x - 0.5) * 12}deg)`;
    el.style.setProperty('--lx', `${50 + (0.5 - x) * 12}%`);
    if (rainbowRef.current) { rainbowRef.current.style.opacity = '1'; rainbowRef.current.style.backgroundPosition = `${x * 100}% ${y * 100}%`; }
    if (reflectionRef.current) { reflectionRef.current.style.opacity = '1'; reflectionRef.current.style.transform = `translateX(${(x - 0.5) * 200}%)`; }
  }, []);

  const handleMouseLeave = useCallback(() => {
    const el = cardRef.current;
    if (el) el.style.transform = 'perspective(800px) rotateY(0deg)';
    if (el) { el.style.setProperty('--lx', '50%'); el.style.setProperty('--ly', '20%'); }
    if (rainbowRef.current) rainbowRef.current.style.opacity = '0.5';
    if (reflectionRef.current) reflectionRef.current.style.opacity = '0.25';
  }, []);

  return (
    <div className="bdc-card-outer" ref={interactive ? cardRef : undefined} onMouseMove={interactive ? handleMouseMove : undefined} onMouseLeave={interactive ? handleMouseLeave : undefined} style={{ transition: interactive ? 'transform 0.25s ease-out' : 'none', ...(scale !== 1 ? { width: `${320 * scale}px`, height: `${450 * scale}px`, overflow: 'hidden' } : {}) }}>
      <div className="bdc-card-scaler" style={scale !== 1 ? { transform: `scale(${scale})`, transformOrigin: 'top left' } : undefined}>
      <div className={`bdc-card-flipper ${flipped ? 'flipped' : ''}`} onClick={interactive ? () => setFlipped(!flipped) : undefined}>

        {/* FRONT */}
        <div className="bdc-card-face bdc-card-front bdc-rare999-front">
          <div className="bdc-card-bg" style={{ background: `linear-gradient(160deg, ${teamColor} 0%, ${teamColor}cc 40%, ${teamColor}99 100%)` }} />
          {interactive && <>
            <div className="bdc-rare999-refractor-lines" />
            <div className="bdc-chrome-specular bdc-chrome-mask" />
            <div ref={rainbowRef} className="bdc-chrome-rainbow bdc-chrome-mask" />
            <div ref={reflectionRef} className="bdc-chrome-reflection bdc-chrome-mask" />
            <div className="bdc-chrome-edge" />
          </>}
          <div className="bdc-photo-area">
            <img src={actionUrl} alt={name} style={{ objectPosition: photoPosition }} />
          </div>
          {/* Numbering */}
          <div className="bdc-numbering bdc-rare999-numbering">{numbering}</div>
          <div className="bdc-bottom-bar bdc-rare999-bar">
            <div className="bdc-names-row">
              <div className="bdc-names-outline" style={{ background: 'linear-gradient(135deg, #c0c0d8, #e0e0f0, #a8a8c8)' }} />
              <div className="bdc-names-fill" />
              <span className="bdc-player-name" style={{ fontSize: `${fontSize}px` }}>{name}</span>
            </div>
            <div className="bdc-description"><em>{teamFull}</em></div>
          </div>
          <img className="bdc-team-logo" src={teamLogoUrl} alt={team} onError={(e) => e.target.style.display='none'} />
          <div className="bdc-bd-logo">
            <div className="bdc-bd-bar" />
            <div className="bdc-bd-tick" />
            BD
          </div>
        </div>

        {/* BACK */}
        {(interactive || showBack) && (
        <div className="bdc-card-face bdc-card-back">
          <div className="bdc-back-header" style={{ borderBottomColor: nameplateColor || teamColor }}>
            <img className="bdc-back-headshot" src={headshotUrl} alt={name} style={{ borderColor: nameplateColor || teamColor }} onError={(e) => e.target.style.display='none'} />
            <div className="bdc-back-header-info">
              <div className="bdc-back-player-name">{name}</div>
              <div className="bdc-back-player-meta">{teamFull} · {position} · #{number}</div>
            </div>
            <img className="bdc-back-team-logo" src={teamLogoUrl} alt={team} onError={(e) => e.target.style.display='none'} />
          </div>

          <div className="bdc-bio-row">
            {bio.born && <div className="bdc-bio-item"><div className="bdc-bio-label">Born</div><div className="bdc-bio-value">{bio.born}</div></div>}
            {bio.height && <div className="bdc-bio-item"><div className="bdc-bio-label">Height</div><div className="bdc-bio-value">{bio.height}</div></div>}
            {bio.weight && <div className="bdc-bio-item"><div className="bdc-bio-label">Weight</div><div className="bdc-bio-value">{bio.weight}</div></div>}
            {bio.batsThrows && <div className="bdc-bio-item"><div className="bdc-bio-label">Bats/Throws</div><div className="bdc-bio-value">{bio.batsThrows}</div></div>}
          </div>

          <div className="bdc-stats-title" style={{ color: nameplateColor || teamColor }}>Season Stats</div>
          <table className="bdc-stats-table">
            <thead><tr>{statHeaders.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
            <tbody>
              {stats.map((row, i) => (
                <tr key={i} className={i === stats.length - 1 ? 'highlight' : ''}>
                  {row.map((cell, j) => <td key={j}>{cell}</td>)}
                </tr>
              ))}
            </tbody>
          </table>

          {highlights.length > 0 && (
            <>
              <div className="bdc-stats-title" style={{ color: nameplateColor || teamColor }}>Career Highlights</div>
              <table className="bdc-stats-table">
                <thead><tr><th style={{ textAlign: 'left' }}>Achievement</th><th>Year</th></tr></thead>
                <tbody>
                  {highlights.map((h, i) => (
                    <tr key={i}><td>{h.achievement}</td><td>{h.year}</td></tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <div className="bdc-back-footer">
            <div className="bdc-card-number">{cardNumber}</div>
            <div className="bdc-rarity-tag" style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>Rare {numbering}</div>
          </div>
        </div>
        )}

      </div>
      </div>
    </div>
  );
};

export default React.memo(Rare999Card);
